import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useSpring, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, Globe } from "lucide-react";
import Logo from "./Logo";
import MobileMenu from "./MobileMenu";
import { NAV_LINKS } from "../data/content";

const LangSwitcher = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseLeave={() => setOpen(false)}>
      <button
        data-testid="lang-switcher-button"
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setOpen(true)}
        className="flex items-center gap-1.5 label-tech px-3 py-2.5 rounded-full text-graphite hover:text-coal hover:bg-white/60 transition-colors duration-300"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Globe size={13} strokeWidth={2} /> EN
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 300, damping: 24 }}
            className="absolute right-0 top-full mt-2 w-36 glass rounded-2xl p-2"
            data-testid="lang-switcher-dropdown"
          >
            {["EN", "DE", "FR", "IT"].map((l) => (
              <div
                key={l}
                data-testid={`lang-option-${l.toLowerCase()}`}
                className={`flex items-center justify-between px-3 py-2 rounded-xl text-xs font-semibold tracking-[0.14em] ${
                  l === "EN" ? "text-coal bg-white/60" : "text-graphite/50 cursor-not-allowed"
                }`}
              >
                {l}
                {l !== "EN" && <span className="text-[9px] uppercase tracking-[0.14em] text-cobalt">Soon</span>}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 28, mass: 0.4 });

  useEffect(() => setMenuOpen(false), [location.pathname]);

  return (
    <>
      <motion.div
        data-testid="scroll-progress"
        className="fixed top-0 left-0 right-0 h-[2px] bg-electric z-[70] origin-left"
        style={{ scaleX: progress }}
      />
      <header data-testid="site-header" className="fixed top-3 sm:top-4 left-0 right-0 z-[60]">
        <div className="container-ax">
          <div className="glass rounded-full h-[62px] pl-5 pr-2.5 flex items-center justify-between">
            <Logo />
            <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-${l.label.toLowerCase()}`}
                  className={`label-tech px-4 py-2.5 rounded-full transition-colors duration-300 ${
                    location.pathname.startsWith(l.to)
                      ? "bg-coal text-paper"
                      : "text-graphite hover:text-coal hover:bg-white/60"
                  }`}
                >
                  {l.label}
                </Link>
              ))}
            </nav>
            <div className="flex items-center gap-2">
              <div className="hidden sm:block">
                <LangSwitcher />
              </div>
              <Link
                to="/contact"
                data-testid="header-cta"
                className="hidden md:inline-flex items-center gap-2 label-tech px-5 py-3 rounded-full bg-coal text-paper hover:bg-cobalt transition-colors duration-500"
              >
                Discuss Your Project <ArrowUpRight size={14} />
              </Link>
              <button
                data-testid="mobile-menu-button"
                onClick={() => setMenuOpen(true)}
                aria-label="Open menu"
                className="lg:hidden p-2.5 rounded-full bg-white/60 text-coal transition-colors hover:bg-white/80"
              >
                <Menu size={22} />
              </button>
            </div>
          </div>
        </div>
      </header>
      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
};

export default Header;
